import { native } from '@forest/native-services'
import { Dialog as SharedDialog } from '@forest/shared/solid'
import { useQuery, useQueryClient } from '@tanstack/solid-query'
import {
  createEffect,
  createSignal,
  Match,
  onCleanup,
  Show,
  Switch,
  untrack,
} from 'solid-js'
import { BootSkeleton } from '~/components/boot-skeleton'
import { BootstrapErrorDialog } from '~/components/bootstrap-error-dialog'
import { DailyPicksRefreshDialogs } from '~/components/daily-picks-refresh-dialogs'
import {
  createHeroBanners,
  HeroBannersContext,
} from '~/components/hero-banners'
import { PurchaseCommittedContext } from '~/components/purchase-flow/committed'
import { ShopShell } from '~/components/shop-shell'
import {
  launchProductParams,
  parseDeepLinkTarget,
  resolveLandingTab,
  selectLaunchProduct,
} from '~/lib/deep-link'
import { logNonFatal } from '~/lib/diagnostics'
import {
  landSkinIdParam,
  serverTimestampSeedMs,
  supportsNativePaywall,
  tabParam,
  treeIdParam,
} from '~/lib/params'
import { mark, markOnce, timed } from '~/lib/perf'
import { preloadImage } from '~/lib/preload-image'
import { createRevealGate, REVEAL_DEADLINE_MS } from '~/lib/reveal'
import {
  isInSubscription,
  refreshServiceLevelAfterPaywall,
  resolvePlusState,
  shopBootstrapQuery,
  trialEligibilityQuery,
  type ShopBootstrap,
  type ShopCollectionCode,
} from '~/services/bootstrap'
import {
  landSkinDetailAssetsQuery,
  treeDetailAssetsQuery,
} from '~/services/catalogue'
import { currencyCatalogueQuery } from '~/services/currency-catalogue'
import { dailyPickUnownedCountQuery } from '~/services/daily-picks'
import {
  deriveRefreshTickets,
  refreshCountQuery,
  type RefreshTicket,
} from '~/services/refresh-tickets'
import {
  landingSurfaceProgress,
  revealSurfaceReady,
  type RevealSurfaceInput,
} from '~/store/catalogue-grid'
import { createClassicGrid, type ClassicGrid } from '~/store/classic-grid'
import { createClassicReveal } from '~/store/classic-reveal'
import { createDailyPickRollover } from '~/store/daily-pick-rollover'
import { createDailyPicksRefresh } from '~/store/daily-picks-refresh'
import { createDailyPicksRefreshFlow } from '~/store/daily-picks-refresh-flow'
import {
  createDailyPicksGrid,
  type DailyPicksGrid,
} from '~/store/daily-picks-grid'
import { createHeroRow, type HeroRowState } from '~/store/hero-row'
import { createHydrationOrder } from '~/store/hydration-order'
import { createServerClock } from '~/store/server-clock'
import { createSessionLedger } from '~/store/session-ledger'
import { createStarterPack, type StarterPackState } from '~/store/starter-pack'

export function App() {
  // The native loading cover goes as soon as Shop runs, before Shop
  // bootstrap: from the first frame the boot skeleton is Shop's loading UI
  // (GLOSSARY.md "Boot skeleton"; ADR 0048). Called here rather than in
  // setup.ts so it follows the mock bridge in mock mode; App renders right
  // after the bridge initializes.
  void timed('pageReady', native.general.pageReady())
  const queryClient = useQueryClient()
  const bootstrap = useQuery(() => shopBootstrapQuery())
  // One clock for every domain time judgment (GLOSSARY.md "Shop server
  // clock"): sale windows today, the featured countdowns later.
  const serverNow = createServerClock(serverTimestampSeedMs)
  // The launch product parameter in force (GLOSSARY.md "Shop launch deep
  // link"), selected once here: the shell resolves it against the grid and
  // the reveal waits on its target.
  const launchProduct = selectLaunchProduct(treeIdParam, landSkinIdParam)
  const deepLinkTarget = parseDeepLinkTarget(launchProduct)
  // Classic for any product deep link, whatever `tab` says: its reveal
  // surface stands in when the target resolves to no visible Classic
  // product (ADR 0048).
  const landingTab = resolveLandingTab(tabParam, launchProduct)
  // The boot skeleton is Shop's only boot loading UI (GLOSSARY.md "Boot
  // skeleton"; ADR 0048). The reveal swaps it for the real Shop once the
  // reveal surface settles or the deadline passes, whichever comes first; a
  // failed bootstrap never reveals. The reveal also releases the second
  // stage of the Shop hydration order (GLOSSARY.md; ADR 0041).
  const gate = createRevealGate()
  const [classicGrid, setClassicGrid] = createSignal<ClassicGrid>()
  // Off a Classic landing, Classic hydrates only once its tab is entered,
  // behind its own catalogue skeleton (GLOSSARY.md "Classic reveal";
  // ADR 0049).
  const classicReveal =
    landingTab === 'classic'
      ? undefined
      : createClassicReveal(() => classicGrid()?.slots)
  const order = createHydrationOrder({
    classicReveal,
    landing: landingTab,
    released: gate.released,
  })
  // Grid-stage hydration starts as soon as bootstrap lands and never
  // restarts: later bootstrap cache writes (the service-level refresh after
  // the Plus paywall) must not re-run it. Slots fill in place as each tree
  // settles (ADR 0024); the launch deep link's target hydrates first, and
  // behind the boot skeleton only the reveal surface's slots issue native
  // calls. The Daily Picks grid and the hero row hydrate alongside under the
  // same rule.
  const [dailyPicks, setDailyPicks] = createSignal<DailyPicksGrid>()
  const [heroRow, setHeroRow] = createSignal<HeroRowState>()
  // The starter pack's hero slot (GLOSSARY.md "Starter pack (新手禮包)"),
  // created with the hero row so the row never shifts (ADR 0024).
  const [starterPack, setStarterPack] = createSignal<StarterPackState>()
  // The refresh tickets derive once with the grids (GLOSSARY.md "Refresh
  // ticket"): a later bootstrap cache write must not re-report omissions.
  const [refreshTickets, setRefreshTickets] = createSignal<RefreshTicket[]>()
  // The launch deep link's detail dialog is part of the reveal surface
  // (GLOSSARY.md "Reveal surface"): presented — assets settled, loaded or
  // failed, images decoded, enter animation finished — before the reveal may
  // land on it. The shell's detail dialog reports it.
  const [targetDetailPresented, setTargetDetailPresented] = createSignal(false)
  const presentTargetDetail = () => {
    markOnce('detail:presented')
    setTargetDetailPresented(true)
  }
  createEffect(() => {
    const data = bootstrap.data
    if (!data || classicGrid()) return
    setRefreshTickets(
      deriveRefreshTickets(data.collections['shop-daily-refresh']),
    )
    // Creation order is enqueue order behind the boot skeleton: the deep-link
    // target's detail assets and Classic first so the target leads the
    // Classic surface a product deep link lands on, then the hero row ahead
    // of Daily Picks. The detail assets are prefetched here by gid
    // rather than on dialog mount, where they queued behind the landing
    // surface; the dialog's own query picks the prefetch up by key. A gid
    // that resolves to no target wastes the calls — a broken deep link,
    // already reported as unresolved.
    if (deepLinkTarget !== undefined) {
      const { gid } = deepLinkTarget
      const detail =
        deepLinkTarget.content === 'tree_type'
          ? queryClient.fetchQuery(treeDetailAssetsQuery(gid))
          : queryClient.fetchQuery(landSkinDetailAssetsQuery(gid))
      void detail.then(
        () => markOnce('detail-assets:settled', { error: false }),
        () => markOnce('detail-assets:settled', { error: true }),
      )
    }
    const classic = createClassicGrid(data, {
      gate: order.classic,
      target: deepLinkTarget,
    })
    // A land-skin target's showing slide is a remote URL known from
    // bootstrap, and the reveal waits for it (GLOSSARY.md "Reveal surface"):
    // warm the browser cache alongside the slot's own hydration rather than
    // on dialog open. The other slides load with the dialog; a failure here
    // is the dialog's to report when it loads the slide itself.
    if (classic.target?.content === 'land_skin') {
      void preloadImage(
        classic.target.landSkinArt.detailSlides[0].imageUrl,
      ).catch(() => undefined)
    }
    // The starter pack leads the hero row until known owned, so its lookups
    // enqueue first.
    const pack = createStarterPack(data, data.currentUser.id, {
      gate: order.heroRow,
    })
    const heroes = createHeroRow(data, serverNow(), { gate: order.heroRow })
    const daily = createDailyPicksGrid(data, { gate: order.dailyPicks })
    setClassicGrid(classic)
    setDailyPicks(daily)
    setStarterPack(pack)
    setHeroRow(heroes)
    // Slot counts size the native-call burst that follows (three lookups per
    // tree slot, two per land skin; Classic's off a Classic landing only
    // once its tab is entered); untracked so the counts add no dependency to
    // this effect.
    untrack(() =>
      mark('hydration:start', {
        classic_slots: classic.slots.length,
        daily_pick_slots: daily.slots.length,
        hero_slots: heroes.gridSlots.length,
        landing_tab: landingTab,
      }),
    )
  })
  // The refresh count (GLOSSARY.md): one read per page load, off the critical
  // path, keyed by the tickets' shared item type; nothing to read without a
  // valid ticket. Each committed refresh adds one locally; the rollover
  // resets it to zero — the server's day has turned.
  const refreshCount = useQuery(() =>
    refreshCountQuery(bootstrap.data, refreshTickets()?.[0]),
  )
  createEffect(() => {
    const error = refreshCount.error
    if (!error) return
    logNonFatal('shop_refresh_count_failed', { detail: error.message })
  })
  const dailyPickUnownedCount = useQuery(() =>
    dailyPickUnownedCountQuery(bootstrap.data?.currentUser.id),
  )
  createEffect(() => {
    const error = dailyPickUnownedCount.error
    if (!error) return
    logNonFatal('shop_daily_pick_unowned_count_failed', {
      detail: error.message,
    })
  })
  const refresh = createDailyPicksRefresh({
    countFailed: () => refreshCount.isError,
    seedCount: () => refreshCount.data,
    tickets: refreshTickets,
  })
  // The daily-pick rollover (ADR 0028): the midnight mid-session refetch. At
  // each UTC midnight on the server clock it hydrates a replacement Daily
  // Picks grid off-screen and swaps the signal once settled; a failure fails
  // closed into the same general error dialog as bootstrap. The swap also turns the
  // refresh count over to the new day.
  const rollover = createDailyPickRollover({
    bootstrap: () => bootstrap.data,
    onSwap: (grid) => {
      setDailyPicks(grid)
      refresh.resetCount()
    },
    serverNow,
  })
  // Currency hydration and trial eligibility resolve off the bootstrap
  // critical path (ADR 0023): the Coins & Crystals tab shows sectioned
  // skeletons and a Plus CTA skeleton while they do. Currency follows the
  // hydration order — the reveal surface on a top_up landing, second-stage
  // work otherwise (ADR 0041); trial eligibility is outside the order and
  // starts at bootstrap.
  const currency = useQuery(() => ({
    ...currencyCatalogueQuery(bootstrap.data!),
    enabled: bootstrap.data !== undefined && order.currencyEnabled(),
  }))
  const trialEligibility = useQuery(() => ({
    ...trialEligibilityQuery(bootstrap.data!),
    // Without the native paywall capability the Plus row never renders, and
    // a subscriber's row is 'active' regardless — eligibility is not
    // determined at all in either case.
    enabled:
      supportsNativePaywall &&
      bootstrap.data !== undefined &&
      !isInSubscription(bootstrap.data.serviceLevelStatus),
  }))
  createEffect(() => {
    const error = trialEligibility.error
    if (!error) return
    logNonFatal('shop_trial_eligibility_failed', { detail: error.message })
  })
  // Off the critical path, but they share the bridge with grid hydration
  // while it runs; the perf timeline shows when they settled.
  createEffect(() => {
    if (currency.data !== undefined || currency.isError) {
      markOnce('currency:settled', { error: currency.isError })
    }
  })
  createEffect(() => {
    if (trialEligibility.data !== undefined || trialEligibility.isError) {
      markOnce('trial-eligibility:settled', { error: trialEligibility.isError })
    }
  })

  const currencySettled = () => currency.data !== undefined || currency.isError
  // On a Featured landing the whole tab is the reveal surface (GLOSSARY.md
  // "Reveal surface"): beyond the slots, every hero card's banner loaded or
  // failed — each card reports its own — and the unowned count, which
  // decides the Daily Picks section's final shape.
  const heroBanners = createHeroBanners()
  const dailyPickUnownedCountSettled = () =>
    dailyPickUnownedCount.data !== undefined || dailyPickUnownedCount.isError
  // Every hero-row slot in row order, the starter pack's leading: on a
  // Featured landing the whole row is the reveal surface (GLOSSARY.md
  // "Starter pack (新手禮包)").
  const heroRowSlots = (heroes: HeroRowState) => {
    const pack = starterPack()?.slot()
    return pack ? [pack, ...heroes.gridSlots] : heroes.gridSlots
  }
  // The reveal predicate's input, once the grids exist: read by the reveal
  // effect and by the deadline's progress snapshot, so the two see the same
  // surface.
  const revealSurface = (): RevealSurfaceInput | undefined => {
    const grid = classicGrid()
    const dailyPicksGrid = dailyPicks()
    const heroes = heroRow()
    if (!grid || !dailyPicksGrid || !heroes) return undefined
    return {
      currencySettled: currencySettled(),
      dailyPickSlots: dailyPicksGrid.slots,
      dailyPickUnownedCountSettled: dailyPickUnownedCountSettled(),
      heroBanners: heroBanners.statuses(),
      heroSlots: heroRowSlots(heroes),
      landing: landingTab,
      slots: grid.slots,
      target:
        deepLinkTarget === undefined
          ? undefined
          : { ...deepLinkTarget, detailPresented: targetDetailPresented() },
    }
  }
  createEffect(() => {
    const surface = revealSurface()
    if (!surface) return
    // Perf timeline only: how long the landing surface's first member (a
    // slot, or on Featured also a banner or the unowned count) took.
    // Untracked so it adds no dependency to the reveal predicate.
    untrack(() => {
      const { settled, size } = landingSurfaceProgress(surface)
      if (settled > 0) {
        markOnce('hydration:first-slot-settled', {
          landing_tab: landingTab,
          settled_window: `${settled}/${size}`,
        })
      }
    })
    if (revealSurfaceReady(surface)) gate.reveal('landing-settled')
  })
  // The deadline clock starts when hydration starts (bootstrap success); a
  // stalled bootstrap is bounded by the API client's request timeout and
  // fails into its error dialog over the boot skeleton (ADR 0048).
  createEffect(() => {
    if (!classicGrid() || !dailyPicks() || !heroRow()) return
    // The rollover swap re-runs this effect long after the reveal; a
    // revealed gate needs no new deadline timer. Untracked: the reveal
    // itself must not re-run the deadline effect.
    if (untrack(gate.revealed)) return
    const deadline = setTimeout(() => {
      if (!gate.reveal('deadline')) return
      const surface = revealSurface()!
      const { settled, size } = landingSurfaceProgress(surface)
      logNonFatal('shop_reveal_deadline_hit', {
        landing_tab: landingTab,
        settled_window: `${settled}/${size}`,
        currency_settled: String(surface.currencySettled),
        ...launchProductParams(launchProduct),
        ...(surface.target === undefined
          ? {}
          : { detail_presented: String(surface.target.detailPresented) }),
      })
    }, REVEAL_DEADLINE_MS)
    onCleanup(() => clearTimeout(deadline))
  })
  const plusState = () =>
    bootstrap.data
      ? resolvePlusState(
          bootstrap.data.serviceLevelStatus,
          trialEligibility.data,
        )
      : undefined
  const ledger = createSessionLedger(() => bootstrap.data?.currentUser)
  // The Daily Picks refresh (GLOSSARY.md, ADR 0032): the paid re-draw, one
  // operation spanning the ticket purchase and the same refetch the rollover
  // runs. It lives out here with the grid signal, the ledger, and the
  // rollover it shares them with — above the tab panels, so a tab switch
  // mid-refresh neither cancels nor restarts it.
  const refreshFlow = createDailyPicksRefreshFlow({
    bootstrap: () => bootstrap.data,
    dailyPickSlotCount: () => dailyPicks()?.slots.length ?? 0,
    onCommitted: (ticket) => {
      ledger.debit(ticket.currency, ticket.price)
      refresh.countRefresh()
    },
    onSwap: setDailyPicks,
    serverNow,
  })
  // Skeleton card counts come from the already-loaded collection entries;
  // only read under the Match that guarantees bootstrap data.
  const collectionCount = (code: ShopCollectionCode) =>
    bootstrap.data!.collections[code].item_type_product_collection_products
      .length

  // Every committed purchase through the Shop purchase flow refetches the
  // starter pack (GLOSSARY.md "Starter pack (新手禮包)"), so a user who
  // qualifies mid-session gets the card without reopening Shop. The Daily
  // Picks refresh runs its own flow and does not. A purchase of the pack
  // itself also turns it owned at once, whatever the refetch finds.
  const onPurchaseCommitted = (productId: number) => {
    const pack = starterPack()
    pack?.markBought(productId)
    void pack?.refetch()
  }

  const refreshServiceLevel = async () => {
    const current = bootstrap.data
    if (!current) return
    const serviceLevelStatus = await refreshServiceLevelAfterPaywall(
      current.currentUser.id,
    )
    queryClient.setQueryData<ShopBootstrap>(
      shopBootstrapQuery().queryKey,
      (previous) => (previous ? { ...previous, serviceLevelStatus } : previous),
    )
  }

  return (
    <HeroBannersContext.Provider value={heroBanners}>
      {/* The real Shop mounts as soon as its data is ready, beneath the boot
          skeleton overlay below; a failed bootstrap's error dialog opens over
          the skeleton, which then never leaves. */}
      <Switch>
        <Match when={bootstrap.error}>
          {(error) => <BootstrapErrorDialog code={error().message} />}
        </Match>
        <Match
          when={
            bootstrap.data &&
            ledger.balances() &&
            classicGrid() &&
            dailyPicks() &&
            heroRow() &&
            starterPack()
          }
        >
          <PurchaseCommittedContext.Provider value={onPurchaseCommitted}>
            <ShopShell
              balances={ledger.balances}
              classicRevealed={classicReveal?.revealed ?? (() => true)}
              classicSlots={classicGrid()!.slots}
              credit={ledger.credit}
              currencyCatalogue={currency.data}
              currencySkeletonCounts={{
                coin: collectionCount('shop-coin'),
                gem: collectionCount('shop-gem'),
              }}
              dailyPickUnownedCount={dailyPickUnownedCount.data}
              dailyPickResponseSize={dailyPicks()!.responseSize}
              dailyPickSlots={dailyPicks()!.slots}
              heroSlots={heroRow()!.heroSlots}
              debit={ledger.debit}
              launchProduct={launchProduct}
              onClassicShowing={classicReveal?.enter}
              onClassicSlotWithinReach={classicGrid()!.reach}
              onDetailPresented={presentTargetDetail}
              onPlusPaywallSuccess={refreshServiceLevel}
              onRefresh={refreshFlow.open}
              plusRowHidden={trialEligibility.isError}
              plusState={plusState()}
              refresh={refresh.state}
              refreshBreathing={refreshFlow.breathing}
              refreshFaceDown={refreshFlow.faceDown}
              refreshLocked={refreshFlow.locked}
              refreshPending={refreshFlow.pending}
              refreshShortage={refreshFlow.shortage}
              revealed={gate.revealed}
              serverNow={serverNow}
              starterPack={starterPack()?.slot()}
              starterPackRefetching={() => starterPack()?.refetching() ?? false}
              userId={bootstrap.data!.currentUser.id}
            />
          </PurchaseCommittedContext.Provider>
        </Match>
      </Switch>
      <BootSkeleton revealed={gate.revealed} shape={landingTab} />
      <Show when={rollover.error()}>
        {(code) => <BootstrapErrorDialog code={code()} />}
      </Show>
      <DailyPicksRefreshDialogs balances={ledger.balances} flow={refreshFlow} />
      <SharedDialog />
    </HeroBannersContext.Provider>
  )
}
